import React, { FC, FormEvent, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'
import AuthLayout from './AuthLayout'
import arrowIcon from './assets/arrow.svg'
import { Path } from 'core/Routes'
import { khilanetLogin, khilanetVerifyOtp, khilanetGetUserData, KhilanetAuthError } from './khilanetApi'
import { clearPending, getPending, setPending, signIn, KhilanetPendingState } from './khilanetAuth'

const PRIMARY = '#434FA2'
const TITLE = '#434FA2'
const HELP_TEXT = '#7a838f'
const SECONDARY_TEXT = '#717182'
const INPUT_BG = '#e0e1e1'
const ERROR_RED = '#dc2626'
const LINK_BLUE = '#2563eb'
const LINK_BLUE_2 = '#155dfc'

// Three OTP states the design covers: idle, wrong (transient error), and locked (the user
// used up their attempts and needs to start over).
type OtpState = 'idle' | 'wrong' | 'locked'

const OTP_LENGTH = 6
const MAX_WRONG_TRIES = 3

const Title = styled.h1`
  font-family: liebling, system-ui, sans-serif;
  font-weight: 500;
  font-size: 27px;
  line-height: 36px;
  letter-spacing: 0.3955px;
  color: ${TITLE};
  text-align: center;
  margin: 0;
  max-width: 520px;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 39px;
  width: 100%;
  max-width: 538px;
  margin-top: 22px;
`

const Block = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  width: 100%;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const SmallText = styled.p<{ tone?: 'normal' | 'error' }>`
  margin: 0;
  font-family: liebling, system-ui, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.4px;
  color: ${p => p.tone === 'error' ? ERROR_RED : HELP_TEXT};
  text-align: right;
  width: 100%;
`

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  height: 48px;
  background: ${INPUT_BG};
  border: 0.909px solid ${p => p.hasError ? ERROR_RED : PRIMARY};
  border-radius: 6px;
  padding: 4px 12px;
  font-family: liebling, system-ui, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.4px;
  color: #1f2937;
  text-align: right;
  direction: rtl;
  outline: none;
  letter-spacing: 0.5em;

  &::placeholder {
    color: ${HELP_TEXT};
    letter-spacing: 0.4px;
  }

  &:focus {
    border-color: ${PRIMARY};
    box-shadow: 0 0 0 3px ${PRIMARY}1a;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const SubmitButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: liebling, system-ui, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.4px;
  color: white;
  transition: background 150ms ease;

  ${p => p.disabled ? css`
    background: rgba(67, 79, 162, 0.32);
    cursor: not-allowed;
  ` : css`
    background: ${PRIMARY};
    &:hover { background: #3b4595; }
  `}
`

const Arrow = styled.img`
  width: 12.36px;
  height: 12.36px;
  transform: rotate(-135deg);
`

const SecondaryActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
  font-family: liebling, system-ui, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.4px;
`

const ResendLine = styled.p`
  margin: 0;
  color: ${SECONDARY_TEXT};
`

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: ${LINK_BLUE_2};
  text-decoration: underline;
  &:hover { color: ${LINK_BLUE}; border-bottom: none; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const BackLink = styled(LinkButton)`
  color: ${LINK_BLUE};
`

const Terms = styled.p`
  margin: 0;
  font-family: liebling, system-ui, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.4px;
  color: ${HELP_TEXT};
  text-align: center;

  a {
    color: ${LINK_BLUE};
    text-decoration: underline;
    &:hover { border-bottom: none; }
  }
`

const formatPhone = (raw?: string | null) => {
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '')
  if (digits.length !== 10) return raw
  return `${digits.slice(0, 3)}-${digits.slice(3)}`
}

const OtpKhilanet: FC = () => {
  const h = useHistory()
  const [code, setCode] = useState('')
  const [state, setState] = useState<OtpState>('idle')
  const [tries, setTries] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  // The Login -> OTP bridge: carries the username (for the "code sent to ..." line) and the
  // short-lived 2FA token we verify the code against.
  const [pending, setLocalPending] = useState<KhilanetPendingState | null>(null)

  // Read the bridge on mount. If it's missing the user got here without going through Login —
  // bounce them back so the flow stays coherent.
  useEffect(() => {
    const stored = getPending()
    if (stored && stored.twoFAToken) {
      setLocalPending(stored)
    } else {
      h.replace(Path.KHILANET_LOGIN)
    }
  }, [h])

  const isLocked = state === 'locked'
  const isError = state === 'wrong'
  const canSubmit = code.length === OTP_LENGTH && !submitting && !isLocked && !!pending

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH)
    setCode(next)
    if (state === 'wrong') setState('idle')
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit || !pending) return
    setSubmitting(true)
    try {
      // Verify the code -> consent token, then load the profile, then persist the session.
      const otp = await khilanetVerifyOtp(pending.twoFAToken, code)
      let user
      try {
        user = await khilanetGetUserData(otp.consentToken)
      } catch {
        // Soft-fail: the panel can run with token + phone only; the data provider falls back
        // to placeholder profile bits. (Logged so it's noticeable in dev.)
        console.warn('[Khilanet auth] userData failed after OTP — signing in without profile')
      }
      signIn({
        phone: pending.phone,
        accessToken: otp.consentToken,
        refreshToken: otp.refreshToken,
        expiresAt: otp.expiresAt,
        user,
      })
      clearPending()
      h.replace(Path.KHILANET)
    } catch (err) {
      if (err instanceof KhilanetAuthError && err.kind === 'unauthorized') {
        // The 2FA token is expired/invalid — send them back to request a fresh code.
        clearPending()
        h.replace(Path.KHILANET_LOGIN)
        return
      }
      // wrong-otp (or anything else) — count the attempt; lock after MAX_WRONG_TRIES.
      const nextTries = tries + 1
      setTries(nextTries)
      setState(nextTries >= MAX_WRONG_TRIES ? 'locked' : 'wrong')
    } finally {
      setSubmitting(false)
    }
  }

  const handleResend = async () => {
    if (isLocked || !pending || submitting) return
    setCode('')
    setState('idle')
    try {
      const { twoFAToken, otpType } = await khilanetLogin(pending.phone)
      const next: KhilanetPendingState = { ...pending, twoFAToken, otpType, requestedAt: Date.now() }
      setPending(next)
      setLocalPending(next)
    } catch {
      // Best-effort — the previously sent code is usually still valid for a while.
    }
  }

  const handleBack = () => {
    clearPending()
    h.push(Path.KHILANET_LOGIN)
  }

  const aboveText = pending?.phone
    ? `קוד האימות נשלח למספר ${formatPhone(pending.phone)}`
    : 'קוד האימות נשלח למספר שהזנתם'
  const belowText = isLocked
    ? 'חרגת ממספר הניסיונות המותר. חזרו להתחלה כדי לקבל קוד חדש.'
    : isError
      ? 'הקוד שגוי, נסו שוב או בקשו קוד חדש.'
      : 'הקלידו את קוד האימות שקיבלתם והקליקו על אישור לכניסה לאתר'

  return (
    <AuthLayout>
      <Title>{`לקוחות 'מקומי' ו'קהילה-נט', ברוכים הבאים לאתר ההטבות של 'מקומי-נט'`}</Title>
      <Form onSubmit={handleSubmit} noValidate>
        <Block>
          <FieldGroup>
            <SmallText>{aboveText}</SmallText>
            <Input
              autoFocus
              value={code}
              onChange={handleChange}
              placeholder="קוד אימות (6 ספרות)"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={OTP_LENGTH}
              hasError={isError || isLocked}
              disabled={isLocked}
              aria-invalid={isError || isLocked}
              aria-describedby="otp-helper"
            />
            <SmallText id="otp-helper" tone={isError || isLocked ? 'error' : 'normal'}>
              {belowText}
            </SmallText>
          </FieldGroup>
          <SubmitButton type="submit" disabled={!canSubmit}>
            <Arrow src={arrowIcon} alt="" />
            אישור
          </SubmitButton>
          <SecondaryActions>
            <ResendLine>
              לא קיבלתי את קוד האימות,{' '}
              <LinkButton type="button" onClick={handleResend} disabled={isLocked || submitting}>
                שלחו שוב
              </LinkButton>
            </ResendLine>
            <BackLink type="button" onClick={handleBack}>
              חזרה להתחלה
            </BackLink>
          </SecondaryActions>
        </Block>
        <Terms>
          בכניסה לאתר אני מאשר/ת את{' '}
          <a href="#">תנאי השימוש</a>
          {' ו'}
          <a href="#">מדיניות הפרטיות</a>
        </Terms>
      </Form>
    </AuthLayout>
  )
}

export default OtpKhilanet
